import {
  DestinationEntityRecordStatus,
  FailedDestinationEntityRecord,
  MergeConflictDestinationEntityRecord,
  SkippedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityJobRunOptions,
  type IDestinationEntityRecord,
  type IGetRecordResponse,
  type IHasSourceIdentifier,
  type IPracticeMigration,
  type ISourceEntityRecord,
  type ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Media,
  Patient,
  hasMergeConflicts,
} from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  Firestore,
  FirestoreMigrate,
  ISO_DATE_FORMAT,
  PrimaryKey,
  Timestamp,
  WithPrimaryKey,
  getError,
  resolveSequentially,
  snapshotCombineLatest,
  toMomentTz,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { BaseDestinationEntity } from '../../../destination/base-destination-entity';
import { DestinationEntity } from '../../../destination/destination-entity';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import { PatientIdFilter } from '../../../destination/filters/patient-id-filter';
import { StorageBucket } from '../../../storage';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactMedicalHistoryForm,
  type ExactMedicalHistoryEntry,
} from '../../lib/medical-history-form';
import { ExactMedicalHistoryPdf } from '../../lib/medical-history-pdf';
import {
  ExactStafferMappingHandler,
  resolveExactStaffer,
} from '../mappings/staff';
import {
  ExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import { PatientMedicalHistoriesSourceEntity } from '../../source/entities/patient-medical-histories';
import { PatientDestinationEntity } from './patient';

export const PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE =
  'patientMedicalHistoryDocuments';

const consecutiveNonAlphaNumeric = /[^a-zA-Z0-9]+/g;

export const PATIENT_MEDICAL_HISTORY_DOCUMENTS_DESTINATION_ENTITY =
  DestinationEntity.init({
    metadata: {
      key: PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE,
      label: 'Patient Medical History Documents',
      description:
        'Renders each Exact medical history submission as a PDF and files it against the patient.',
    },
  });

export interface IPatientMedicalHistoryDestinationRecord {
  fileRefs: DocumentReference<IMedia>[];
}

export interface IPatientMedicalHistoryJobData {
  sourcePatient: IGetRecordResponse<ExactPatient>;
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
}

export interface IPatientMedicalHistoryMigrationData {
  sourcePatientId: string;
  patientRef: DocumentReference<IPatient>;
  histories: IPatientMedicalHistoryDocumentData[];
}

export interface IPatientMedicalHistoryDocumentData
  extends IHasSourceIdentifier {
  name: string;
  fileName: string;
  createdAt: Timestamp;
  patientName: string;
  historyDate: string;
  recordedBy?: string;
  entries: ExactMedicalHistoryEntry[];
}

export class PatientMedicalHistoryDocumentsDestinationEntity extends BaseDestinationEntity<
  IPatientMedicalHistoryDestinationRecord,
  IPatientMedicalHistoryJobData,
  IPatientMedicalHistoryMigrationData
> {
  destinationEntity = PATIENT_MEDICAL_HISTORY_DOCUMENTS_DESTINATION_ENTITY;
  override batchLimit = 1000;

  sourceCountComparison = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    medicalHistories: new PatientMedicalHistoriesSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
  };

  override filters = [
    new PatientIdFilter<IPatientMedicalHistoryJobData>((jobData) =>
      this.sourceEntities.patients
        .getSourceRecordId(jobData.sourcePatient.data)
        .toString()
    ),
  ];

  /**
  /* Exact source uids carry punctuation that breaks storage paths.
  /* Collapse each run of those chars to a single dash.
  */
  private static _toFileNameStem(sourceIdentifier: string): string {
    return `medical-history-${sourceIdentifier.replace(
      consecutiveNonAlphaNumeric,
      '-'
    )}`;
  }

  sourceCountDataAccessor(
    data: IPatientMedicalHistoryJobData
  ): PrimaryKey<ISourceEntityRecord> {
    return data.sourcePatient.record.id;
  }

  getDestinationEntityRecordUid(data: IPatientMedicalHistoryJobData): string {
    return data.sourcePatient.record.uid;
  }

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientMedicalHistoryJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));

    return snapshotCombineLatest([staff$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [staff]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          staff,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientMedicalHistoryJobData
  ): Promise<
    | IPatientMedicalHistoryMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
    | (IDestinationEntityRecord & SkippedDestinationEntityRecord)
  > {
    try {
      const sourcePatientId = data.sourcePatient.data.patient_id.toString();
      const patientRef = await translationMap.getDestination<IPatient>(
        sourcePatientId,
        PATIENT_RESOURCE_TYPE
      );

      if (!patientRef) {
        return this._buildErrorResponse(
          data.sourcePatient,
          `Couldn't resolve patient`
        );
      }

      const sourceHistories =
        await this.sourceEntities.medicalHistories.filterRecords(
          migration,
          'patientId',
          sourcePatientId
        );

      const patientName = compact([
        data.sourcePatient.data.first_name,
        data.sourcePatient.data.last_name,
      ]).join(' ');

      const histories = await resolveSequentially(
        sourceHistories,
        async (history) => {
          const historyDate = toMomentTz(
            history.record.translations.historyDate,
            migration.configuration.timezone
          ).format(ISO_DATE_FORMAT);

          // Exact leaves the recording user blank on older submissions.
          // resolveExactStaffer falls back to the default staffer, so an
          // unattributed form would otherwise be credited to someone.
          const recordedBy = history.data.user_id
            ? await resolveExactStaffer(
                history.data.user_id,
                translationMap,
                data.staff
              )
            : undefined;

          return {
            sourceIdentifier: history.record.uid,
            name: `Medical History ${historyDate}`,
            fileName: `${PatientMedicalHistoryDocumentsDestinationEntity._toFileNameStem(
              history.record.uid
            )}.pdf`,
            createdAt: history.record.translations.historyDate,
            patientName,
            historyDate,
            recordedBy: recordedBy?.user.name,
            entries: ExactMedicalHistoryForm.toEntries(history.data.questions),
          };
        }
      );

      return {
        sourcePatientId,
        patientRef,
        histories,
      };
    } catch (error) {
      return this._buildErrorResponse(
        data.sourcePatient,
        `Failed to build migration data: ${getError(error)}`
      );
    }
  }

  async runJob(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMapHandler: TranslationMapHandler,
    jobData: IPatientMedicalHistoryJobData,
    migrationData: IPatientMedicalHistoryMigrationData
  ): Promise<IDestinationEntityRecord> {
    try {
      const migratedFiles = await this.addMedicalHistoriesToPatient(
        migration,
        translationMapHandler,
        migrationData
      );

      return this._buildSuccessResponse(jobData.sourcePatient, migratedFiles);
    } catch (error) {
      return this._buildErrorResponse(jobData.sourcePatient, getError(error));
    }
  }

  buildSkippedResponse(
    data: IPatientMedicalHistoryJobData
  ): IDestinationEntityRecord & SkippedDestinationEntityRecord {
    return {
      uid: data.sourcePatient.record.uid,
      label: data.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Skipped,
      sourceRef: data.sourcePatient.record.id,
    };
  }

  async hasMergeConflict(
    translationMap: TranslationMapHandler,
    data: IPatientMedicalHistoryMigrationData
  ): Promise<IPatientMedicalHistoryMigrationData | undefined> {
    const existingHistories: IPatientMedicalHistoryDocumentData[] = [];

    const historyMergeConflicts = await resolveSequentially(
      data.histories,
      async (history) => {
        const mediaRef = await translationMap.getDestination<IMedia>(
          history.sourceIdentifier,
          PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE
        );
        if (!mediaRef) {
          return;
        }

        const mediaFile = await Firestore.getDoc(mediaRef);
        const existingHistory = {
          ...history,
          name: mediaFile.name,
          createdAt: mediaFile.createdAt,
        };
        existingHistories.push(existingHistory);

        return hasMergeConflicts(history, existingHistory);
      }
    );

    if (historyMergeConflicts.some((conflict) => conflict)) {
      return {
        ...data,
        histories: existingHistories,
      };
    }
  }

  buildMergeConflictRecord(
    _migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    _translationMap: TranslationMapHandler,
    jobData: IPatientMedicalHistoryJobData,
    _migrationData: IPatientMedicalHistoryMigrationData
  ): IDestinationEntityRecord & MergeConflictDestinationEntityRecord {
    return {
      uid: jobData.sourcePatient.record.uid,
      label: jobData.sourcePatient.record.label,
      status: DestinationEntityRecordStatus.MergeConflict,
      sourceRef: jobData.sourcePatient.record.id,
    };
  }

  async addMedicalHistoriesToPatient(
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler,
    migrationData: IPatientMedicalHistoryMigrationData
  ): Promise<DocumentReference<IMedia>[]> {
    const storage = new StorageBucket(
      migration.configuration.projectId,
      migration.configuration.destinationBucket
    );
    const storagePath = Patient.storagePath({ ref: migrationData.patientRef });

    const migratedFiles = await resolveSequentially(
      migrationData.histories,
      async (history) => {
        // A submission whose rows are all placeholders is a blank form, not a
        // failure, so it is left unfiled rather than reported as an error.
        if (!history.entries.length) {
          return;
        }

        const pdf = await ExactMedicalHistoryPdf.render({
          patientName: history.patientName,
          historyDate: history.historyDate,
          recordedBy: history.recordedBy,
          entries: history.entries,
        });
        await storage.uploadFile(pdf, history.fileName, storagePath);

        const mediaDestinationRef = await translationMap.getDestination(
          history.sourceIdentifier,
          PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE
        );

        const mediaRef = await FirestoreMigrate.upsertDoc(
          Patient.mediaCol({ ref: migrationData.patientRef }),
          {
            ...Media.init({
              name: history.name,
              path: `${storagePath}/${history.fileName}`,
            }),
            createdAt: history.createdAt,
          },
          mediaDestinationRef?.id
        );

        if (!mediaDestinationRef) {
          await translationMap.upsert({
            sourceIdentifier: history.sourceIdentifier,
            destinationIdentifier: mediaRef,
            resourceType: PATIENT_MEDICAL_HISTORY_DOCUMENTS_RESOURCE_TYPE,
          });
        }

        return mediaRef;
      }
    );

    return compact(migratedFiles);
  }

  protected _buildErrorResponse(
    sourcePatient: IGetRecordResponse<ExactPatient>,
    errorMessage: string
  ): IDestinationEntityRecord & FailedDestinationEntityRecord {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      status: DestinationEntityRecordStatus.Failed,
      sourceRef: sourcePatient.record.id,
      errorMessage,
      failData: {
        patientRef: sourcePatient.record.id,
      },
    };
  }

  private _buildSuccessResponse(
    sourcePatient: IGetRecordResponse<ExactPatient>,
    fileRefs: DocumentReference<IMedia>[]
  ): IDestinationEntityRecord<IPatientMedicalHistoryDestinationRecord> {
    return {
      uid: sourcePatient.record.uid,
      label: sourcePatient.record.label,
      data: {
        fileRefs,
      },
      status: DestinationEntityRecordStatus.Migrated,
      sourceRef: sourcePatient.record.id,
      migratedAt: toTimestamp(),
    };
  }
}
